import axios from 'axios';
import type { Todo, TodoCreate, TodoUpdate, Team, TeamCreate, TeamUpdate, TeamWithMembers, TeamMemberUpdate, JoinTeamRequest, User, Week, WeekUpdate, WeekTasks, Month, MonthUpdate, MonthTasks, Year, YearUpdate, TodoComment, TodoCommentCreate, TodoCommentUpdate, Event, EventCreate, EventUpdate, HabitWithCompletions, HabitCreate, HabitUpdate, HabitCompletion, Goal, GoalWithLinkedItems, GoalCreate, GoalUpdate, JournalEntry, JournalEntryCreate, JournalEntryUpdate, JournalEntrySource, Document, DocumentCreate, DocumentUpdate, FocusSession, FocusSessionCreate, FocusSessionUpdate, FocusSessionItem, FocusSessionItemCreate } from '../types';
import type { PromptCategory } from '../data/prompts';
import { authService } from './auth';

// Configure API base URL based on environment
// Dynamically detect if accessing from network IP or localhost
const getApiUrl = () => {
  // Check if we have an explicit API URL set
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // In production, always use the production API
  if (!import.meta.env.DEV) {
    return 'https://api.zeedaboa.com';
  }
  
  // In development, use the same host as the frontend
  const host = window.location.hostname;
  return `http://${host}:8001`;
};

const API_BASE_URL = getApiUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export the axios instance for components that still use it
export { api };

// Add request interceptor to include auth token if available
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid, logout user only if we have a token
      if (authService.getToken()) {
        authService.logout();
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);


// --- CRUD Factory ---
// Generates standard CRUD methods for a given base path, reducing boilerplate.
// TItem: the entity type returned by getById/update
// TCreate: the type for creating a new entity
// TUpdate: the type for updating an entity
// TList: the type returned by getAll (defaults to TItem[])
// TGetAllParams: the type for getAll query parameters

interface CrudOptions {
  /** Use PATCH instead of PUT for updates */
  usePatch?: boolean;
}

interface CrudAPI<TItem, TCreate, TUpdate, TList = TItem[], TGetAllParams = Record<string, unknown>> {
  getAll: (params?: TGetAllParams) => Promise<TList>;
  getById: (id: string) => Promise<TItem>;
  create: (data: TCreate) => Promise<TItem>;
  update: (id: string, data: TUpdate) => Promise<TItem>;
  delete: (id: string) => Promise<void>;
}

function createCrudAPI<
  TItem,
  TCreate,
  TUpdate,
  TList = TItem[],
  TGetAllParams = Record<string, unknown>,
>(basePath: string, options?: CrudOptions): CrudAPI<TItem, TCreate, TUpdate, TList, TGetAllParams> {
  const updateMethod = options?.usePatch ? 'patch' : 'put';
  return {
    getAll: async (params?: TGetAllParams): Promise<TList> => {
      const response = await api.get(`${basePath}/`, { params });
      return response.data;
    },
    getById: async (id: string): Promise<TItem> => {
      const response = await api.get(`${basePath}/${id}`);
      return response.data;
    },
    create: async (data: TCreate): Promise<TItem> => {
      const response = await api.post(`${basePath}/`, data);
      return response.data;
    },
    update: async (id: string, data: TUpdate): Promise<TItem> => {
      const response = await api[updateMethod](`${basePath}/${id}`, data);
      return response.data;
    },
    delete: async (id: string): Promise<void> => {
      await api.delete(`${basePath}/${id}`);
    },
  };
}

// Helper: creates getCurrent/getAll/getById/update/completeReview for period APIs (weeks, months, years)
function createPeriodAPI<TItem, TUpdate, TTasks = undefined>(
  basePath: string,
  opts?: { hasTasks: boolean },
) {
  const crud = createCrudAPI<TItem, never, TUpdate>(basePath);
  return {
    getCurrent: async (teamId?: string): Promise<TItem> => {
      const params = teamId ? { team_id: teamId } : {};
      const response = await api.get(`${basePath}/current`, { params });
      return response.data;
    },

    getAll: async (teamId?: string, limit = 10, offset = 0): Promise<TItem[]> => {
      const params = {
        limit: limit.toString(),
        offset: offset.toString(),
        ...(teamId && { team_id: teamId }),
      };
      const response = await api.get(`${basePath}/`, { params });
      return response.data;
    },

    getById: crud.getById,
    update: crud.update,

    ...(opts?.hasTasks ? {
      getTasks: async (id: string): Promise<TTasks> => {
        const response = await api.get(`${basePath}/${id}/tasks`);
        return response.data;
      },
    } : {}),

    completeReview: async (id: string): Promise<TItem> => {
      const response = await api.post(`${basePath}/${id}/complete-review`);
      return response.data;
    },
  };
}

// Auth API
export const authAPI = {
  verifyToken: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  googleAuth: async (credential: string): Promise<{ access_token: string; user: User }> => {
    const response = await api.post('/auth/google', {
      token: credential,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return response.data;
  },
};

// Todos API
const todosCrud = createCrudAPI<Todo, TodoCreate, TodoUpdate, Todo[], {
  search?: string;
  status?: string[];
  tags?: string[];
  team_id?: string;
  include_personal?: boolean;
  parent_only?: boolean;
  child_only?: boolean;
}>('/todos');

export const todosAPI = {
  ...todosCrud,
  get: todosCrud.getById,

  getSubtasks: async (parentId: string): Promise<Todo[]> => {
    const response = await api.get(`/todos/${parentId}/subtasks`);
    return response.data;
  },
};

// Teams API
const teamsCrud = createCrudAPI<Team, TeamCreate, TeamUpdate, TeamWithMembers[]>('/teams');

export const teamsAPI = {
  ...teamsCrud,
  // getById returns TeamWithMembers (richer than base Team)
  getById: async (id: string): Promise<TeamWithMembers> => {
    const response = await api.get(`/teams/${id}`);
    return response.data;
  },

  regenerateJoinCode: async (id: string): Promise<Team> => {
    const response = await api.post(`/teams/${id}/regenerate-code`);
    return response.data;
  },

  joinTeam: async (joinRequest: JoinTeamRequest): Promise<{ success: boolean; team: Team }> => {
    const response = await api.post('/teams/join/', joinRequest);
    return response.data;
  },

  updateMember: async (teamId: string, memberId: string, update: TeamMemberUpdate): Promise<{ success: boolean }> => {
    const response = await api.put(`/teams/${teamId}/members/${memberId}`, update);
    return response.data;
  },

  removeMember: async (teamId: string, memberId: string): Promise<void> => {
    await api.delete(`/teams/${teamId}/members/${memberId}`);
  },

  getMembers: async (teamId: string): Promise<User[]> => {
    const response = await api.get(`/teams/${teamId}/members`);
    return response.data;
  },
};

// Reflection Prompts API
export const promptsAPI = {
  getDefaults: async (category: PromptCategory): Promise<string[]> => {
    const response = await api.get(`/prompts/defaults/${category}`);
    return response.data;
  },

  getInspiration: async (category: string): Promise<string[]> => {
    const response = await api.get(`/prompts/inspiration/${category}`);
    return response.data;
  },

  getInspirationRandom: async (category: string): Promise<{ prompt: string }> => {
    const response = await api.get(`/prompts/inspiration/${category}/random`);
    return response.data;
  },
};

// Months API
export const monthsAPI = createPeriodAPI<Month, MonthUpdate, MonthTasks>('/months', { hasTasks: true });

// Weeks API
export const weeksAPI = createPeriodAPI<Week, WeekUpdate, WeekTasks>('/weeks', { hasTasks: true });

// Years API
export const yearsAPI = createPeriodAPI<Year, YearUpdate>('/years');

// AI API
export interface AIToolResult {
  tool: string;
  arguments: Record<string, unknown>;
  result: Record<string, unknown>;
  success: boolean;
}

export interface AIDebugLogEntry {
  t: number;
  event: string;
  [key: string]: unknown;
}

export interface AIChatResponse {
  success: boolean;
  content: string;
  conversation_id?: string;
  tool_results?: AIToolResult[];
  thinking_text?: string;
  usage?: { input_tokens: number; output_tokens: number };
  model?: string;
  error?: string;
  debug_log?: AIDebugLogEntry[];
  retry_after?: number;
}

export interface AIModel {
  id: string;
  name: string;
  model_id: string;
}

export interface LocalAgentRun {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'interrupted';
  conversation_id: string | null;
  result: AIChatResponse | null;
}

export interface LocalAgentStatus {
  online: boolean;
  devices: { id: string; name: string; last_seen: string | null }[];
}

export const localAgentAPI = {
  status: async (): Promise<LocalAgentStatus> => (await api.get('/local-agent/status')).data,
  approve: async (code: string) => (await api.post('/local-agent/pair/approve', { code })).data,
  revoke: async (id: string) => (await api.delete(`/local-agent/devices/${id}`)).data,
  submit: async (body: { request_key: string; message: string; model: string; conversation_id?: string; task_id?: string; focus_session_id?: string }): Promise<LocalAgentRun> => {
    // Only transport/server failures retry, always with the original request ID.
    try {
      return (await api.post('/local-agent/runs', body, { timeout: 30000 })).data;
    } catch (error) {
      if (axios.isAxiosError(error) && (!error.response || error.response.status >= 500)) {
        return (await api.post('/local-agent/runs', body, { timeout: 30000 })).data;
      }
      throw error;
    }
  },
  getRun: async (id: string): Promise<LocalAgentRun> => (await api.get(`/local-agent/runs/${id}`, { timeout: 15000 })).data,
  getRequest: async (key: string): Promise<LocalAgentRun | null> => (await api.get(`/local-agent/requests/${key}`, { timeout: 15000 })).data,
};

export interface AIConversationMessage {
  role: string;
  content: string | null;
  thinking?: string | null;
  tool_calls?: Array<{ id: string; name: string; arguments: Record<string, unknown> }> | null;
  tool_call_id?: string | null;
  tool_name?: string | null;
}

export interface AIConversation {
  id: string;
  title: string | null;
  model_provider: string | null;
  messages: AIConversationMessage[];
}

export interface AIConversationSummary {
  id: string;
  title: string | null;
  model_provider: string | null;
  is_active: boolean;
  todo_id: string | null;
  created_time: string | null;
  updated_time: string | null;
  message_count: number;
}

export interface AgentAction {
  tool_name: string;
  conversation_id: string;
  created_time: string;
  entity_type: string | null;
  entity_id: string | null;
  entity_title: string | null;
}

export const aiAPI = {
  getConversations: async (todoId?: string): Promise<AIConversationSummary[]> => {
    const params: Record<string, string> = {};
    if (todoId) {
      params.todo_id = todoId;
      params.exclude_task_chats = 'false';
    } else {
      params.exclude_task_chats = 'true';
    }
    const response = await api.get('/ai/conversations', { params });
    return response.data;
  },

  getConversation: async (conversationId: string): Promise<AIConversation> => {
    const response = await api.get(`/ai/conversations/${conversationId}`);
    return response.data;
  },

  getModels: async (): Promise<{ models: AIModel[] }> => {
    const response = await api.get('/ai/models');
    return response.data;
  },

  deleteConversation: async (conversationId: string): Promise<void> => {
    await api.delete(`/ai/conversations/${conversationId}`);
  },

  getActivity: async (date: string): Promise<AgentAction[]> => {
    const response = await api.get('/ai/activity', { params: { date } });
    return response.data.actions;
  },

  runContextReview: async (): Promise<{ digest: string; updated_time: string; usage?: { input_tokens: number; output_tokens: number } }> => {
    const response = await api.post('/ai/context-review');
    return response.data;
  },

  runDailyReview: async (date?: string): Promise<{ review: string; date: string; entry_id: string }> => {
    const response = await api.post('/ai/daily-review', null, { params: date ? { date } : undefined });
    return response.data;
  },

  triggerCheckin: async (): Promise<{
    status: string;
    message: string;
    telegram_sent?: boolean;
    conversation_id?: string;
    tool_calls?: Array<{ tool: string; args: Record<string, unknown>; success: boolean }>;
  }> => {
    const response = await api.post('/ai/trigger-checkin');
    return response.data;
  },

  triggerSession: async (): Promise<{ status: string; message: string }> => {
    const response = await api.post('/ai/trigger-session');
    return response.data;
  },

  getDailyReviews: async (limit?: number): Promise<{ reviews: Array<{ id: string; content: string; period_label: string; created_time: string }> }> => {
    const response = await api.get('/ai/daily-reviews', { params: limit ? { limit } : undefined });
    return response.data;
  },

  getRecentSessions: async (limit?: number): Promise<{ sessions: Array<{ id: string; source: string; title: string; created_time: string | null; content: string | null; tool_calls: number }> }> => {
    const response = await api.get('/ai/recent-sessions', { params: limit ? { limit } : undefined });
    return response.data;
  },

  getDataExport: async (days?: number): Promise<{ text: string; days: number; generated_at: string }> => {
    const response = await api.get('/ai/data-export', { params: days ? { days } : undefined });
    return response.data;
  },

  // Note: getPendingCheckins and dismissCheckin removed — check-in system replaced by autonomous sessions

  getTriageSuggestions: async (taskIds: string[]): Promise<{ suggestions: Array<{
    task_id: string;
    suggested_next_step: string | null;
    suggested_estimated_time: string | null;
    confidence: number;
  }> }> => {
    const response = await api.post('/ai/triage-suggestions', { task_ids: taskIds });
    return response.data;
  },

  getAgentLog: async (params?: { include_cleared?: boolean; days?: number }): Promise<{ entries: Array<{
    id: string;
    content: string;
    created_time: string | null;
    cleared_time: string | null;
    expires_at: string | null;
  }> }> => {
    const response = await api.get('/ai/agent-log', { params });
    return response.data;
  },

  clearAgentLogEntry: async (entryId: string): Promise<void> => {
    await api.delete(`/ai/agent-log/${entryId}`);
  },

  getAgentContext: async (): Promise<{
    rolling_synthesis: { content: string; updated_time: string | null } | null;
    active_conversation_summary: { conversation_id: string; title: string | null; summary: string } | null;
    conversation_summaries: Array<{ conversation_id: string; title: string | null; source: string; summary: string; updated_time: string | null }>;
  }> => {
    const response = await api.get('/ai/agent-context');
    return response.data;
  },

  getDailySyntheses: async (limit?: number): Promise<{ syntheses: Array<{ id: string; date: string; content: string; created_time: string | null }> }> => {
    const response = await api.get('/ai/daily-syntheses', { params: limit ? { limit } : undefined });
    return response.data;
  },

  getToolCallLog: async (params?: { days?: number; tool_name?: string; limit?: number }): Promise<{ entries: Array<{
    id: string;
    tool_name: string;
    arguments_json: string | null;
    result_json: string | null;
    success: boolean;
    duration_ms: number | null;
    conversation_id: string | null;
    created_time: string | null;
  }> }> => {
    const response = await api.get('/ai/tool-call-log', { params });
    return response.data;
  },

  getToolCallStats: async (days?: number): Promise<{
    tool_summary: Array<{ tool_name: string; count: number; success_count: number }>;
    document_reads: Array<{ doc_id: string; title: string | null; read_count: number }>;
    total_calls: number;
    days: number;
  }> => {
    const response = await api.get('/ai/tool-call-stats', { params: days ? { days } : undefined });
    return response.data;
  },

  getActivitySummary: async (date: string): Promise<{
    date: string;
    user: {
      completed_tasks: Array<{ id: string; title: string }>;
      task_changes: Record<string, number>;
    };
    agent: {
      tool_calls: Array<{ tool_name: string; count: number }>;
      total_tool_calls: number;
    };
    habits_completed: number;
  }> => {
    const response = await api.get('/ai/activity-summary', { params: { date } });
    return response.data;
  },
};

// Comments API
export const commentsAPI = {
  getByTodo: async (todoId: string): Promise<TodoComment[]> => {
    const response = await api.get(`/todos/${todoId}/comments`);
    return response.data;
  },

  create: async (todoId: string, comment: TodoCommentCreate): Promise<TodoComment> => {
    const response = await api.post(`/todos/${todoId}/comments`, comment);
    return response.data;
  },

  update: async (commentId: string, comment: TodoCommentUpdate): Promise<TodoComment> => {
    const response = await api.put(`/todos/comments/${commentId}`, comment);
    return response.data;
  },

  delete: async (commentId: string): Promise<void> => {
    await api.delete(`/todos/comments/${commentId}`);
  },
};

// Events API
const eventsCrud = createCrudAPI<Event, EventCreate, EventUpdate, Event[], {
  start_date?: string;
  end_date?: string;
  limit?: number;
  skip?: number;
}>('/events', { usePatch: true });

export const eventsAPI = {
  ...eventsCrud,
  get: eventsCrud.getById,

  getToday: async (): Promise<Event[]> => {
    const response = await api.get('/events/today');
    return response.data;
  },

  getByDate: async (date: string): Promise<Event[]> => {
    const response = await api.get(`/events/date/${date}`);
    return response.data;
  },

  createFromTodo: async (todoId: string, startTime: string, durationMinutes: number = 60): Promise<Event> => {
    const response = await api.post(`/events/from-todo/${todoId}`, null, {
      params: { start_time: startTime, duration_minutes: durationMinutes }
    });
    return response.data;
  },

  getByTodoId: async (todoId: string): Promise<Event[]> => {
    const response = await api.get('/events/', { params: { todo_id: todoId } });
    return response.data;
  },

  syncGoogleCalendar: async () => {
    const response = await api.post('/events/google-calendar/sync');
    return response.data;
  },
};

// Habits API
const habitsCrud = createCrudAPI<HabitWithCompletions, HabitCreate, HabitUpdate, HabitWithCompletions[], {
  active_only?: boolean;
  days?: number;
}>('/habits');

export const habitsAPI = {
  ...habitsCrud,
  get: async (habitId: string, days?: number): Promise<HabitWithCompletions> => {
    const response = await api.get(`/habits/${habitId}`, { params: days ? { days } : undefined });
    return response.data;
  },

  toggle: async (habitId: string, date: string, opts?: { duration_minutes?: number; notes?: string }): Promise<HabitWithCompletions> => {
    const response = await api.post(`/habits/${habitId}/toggle`, { date, ...opts });
    return response.data;
  },

  getCompletions: async (habitId: string, params?: { start_date?: string; end_date?: string }): Promise<HabitCompletion[]> => {
    const response = await api.get(`/habits/${habitId}/completions`, { params });
    return response.data;
  },

  reorder: async (habitIds: string[]): Promise<void> => {
    await api.post('/habits/reorder', { habit_ids: habitIds });
  },
};

// Goals API
const goalsCrud = createCrudAPI<Goal, GoalCreate, GoalUpdate, Goal[], { status?: string }>('/goals');

export const goalsAPI = {
  ...goalsCrud,
  // get returns GoalWithLinkedItems (richer than base Goal)
  get: async (goalId: string): Promise<GoalWithLinkedItems> => {
    const response = await api.get(`/goals/${goalId}`);
    return response.data;
  },
};

// Journal API
const journalCrud = createCrudAPI<JournalEntry, JournalEntryCreate, JournalEntryUpdate, JournalEntry[], {
  source?: JournalEntrySource;
  period_type?: string;
  period_id?: string;
  limit?: number;
  offset?: number;
}>('/journal');

export const journalAPI = {
  ...journalCrud,
  get: journalCrud.getById,

  createBulk: async (entries: JournalEntryCreate[]): Promise<JournalEntry[]> => {
    const response = await api.post('/journal/bulk', { entries });
    return response.data;
  },
};

// Documents API
const documentsCrud = createCrudAPI<Document, DocumentCreate, DocumentUpdate, Document[], {
  owner?: string;
  task_id?: string;
  pinned_only?: boolean;
  folder?: string;
  limit?: number;
}>('/documents');

export const documentsAPI = {
  ...documentsCrud,
  get: documentsCrud.getById,
  getFolders: async (): Promise<Array<{ folder: string; count: number }>> => {
    const response = await api.get('/documents/folders');
    return response.data;
  },
};

// Focus Sessions API
export const focusAPI = {
  getActive: async (): Promise<FocusSession | null> => {
    const response = await api.get('/focus/');
    return response.data;
  },
  getById: async (id: string): Promise<FocusSession> => {
    const response = await api.get(`/focus/${id}`);
    return response.data;
  },
  create: async (data: FocusSessionCreate): Promise<FocusSession> => {
    const response = await api.post('/focus/', data);
    return response.data;
  },
  update: async (id: string, data: FocusSessionUpdate): Promise<FocusSession> => {
    const response = await api.patch(`/focus/${id}`, data);
    return response.data;
  },
  getHistory: async (limit = 20, offset = 0): Promise<FocusSession[]> => {
    const response = await api.get('/focus/history', { params: { limit, offset } });
    return response.data;
  },

  // Items
  addItem: async (sessionId: string, data: FocusSessionItemCreate): Promise<FocusSessionItem> => {
    const response = await api.post(`/focus/${sessionId}/items`, data);
    return response.data;
  },
  removeItem: async (sessionId: string, itemId: string): Promise<void> => {
    await api.delete(`/focus/${sessionId}/items/${itemId}`);
  },
  reorderItems: async (sessionId: string, itemIds: string[]): Promise<FocusSessionItem[]> => {
    const response = await api.put(`/focus/${sessionId}/items/reorder`, { item_ids: itemIds });
    return response.data;
  },
  completeItem: async (sessionId: string, itemId: string): Promise<FocusSessionItem> => {
    const response = await api.post(`/focus/${sessionId}/items/${itemId}/complete`);
    return response.data;
  },
  skipItem: async (sessionId: string, itemId: string): Promise<FocusSessionItem> => {
    const response = await api.post(`/focus/${sessionId}/items/${itemId}/skip`);
    return response.data;
  },
  uncompleteItem: async (sessionId: string, itemId: string): Promise<FocusSessionItem> => {
    const response = await api.post(`/focus/${sessionId}/items/${itemId}/uncomplete`);
    return response.data;
  },
  unskipItem: async (sessionId: string, itemId: string): Promise<FocusSessionItem> => {
    const response = await api.post(`/focus/${sessionId}/items/${itemId}/unskip`);
    return response.data;
  },

  // State transitions
  start: async (id: string): Promise<FocusSession> => {
    const response = await api.post(`/focus/${id}/start`);
    return response.data;
  },
  pause: async (id: string): Promise<FocusSession> => {
    const response = await api.post(`/focus/${id}/pause`);
    return response.data;
  },
  resume: async (id: string): Promise<FocusSession> => {
    const response = await api.post(`/focus/${id}/resume`);
    return response.data;
  },
  complete: async (id: string): Promise<FocusSession> => {
    const response = await api.post(`/focus/${id}/complete`);
    return response.data;
  },
  abandon: async (id: string): Promise<FocusSession> => {
    const response = await api.post(`/focus/${id}/abandon`);
    return response.data;
  },
};
